import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, TextField, Typography, MenuItem, Alert, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from '@mui/material';
import { Save, ArrowBack, Add, Delete } from '@mui/icons-material';
import accountingService from '../../services/accounting.service';
import settingsService from '../../services/settings.service';
import { useLanguage } from '../../i18n/LanguageContext';

interface JournalLine {
  accountId: number | '';
  debitAmount: number;
  creditAmount: number;
  description: string;
}

const emptyLine: JournalLine = { accountId: '', debitAmount: 0, creditAmount: 0, description: '' };

const JournalEntryForm: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const isView = Boolean(id);
  const [error, setError] = useState('');
  const [accounts, setAccounts] = useState<any[]>([]);
  const [currencies, setCurrencies] = useState<any[]>([]);
  const [form, setForm] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    reference: '', description: '', currencyCode: 'USD'
  });
  const [lines, setLines] = useState<JournalLine[]>([{ ...emptyLine }, { ...emptyLine }]);
  const [viewData, setViewData] = useState<any>(null);

  useEffect(() => { loadData(); if (isView) loadEntry(); }, []);

  const loadData = async () => {
    try {
      const accRes = await accountingService.getAccounts();
      setAccounts(accRes.data);
    } catch (err) { console.error('Error loading accounts:', err); }

    try {
      const currRes = await settingsService.getCurrencies();
      setCurrencies(currRes.data);
      const def = currRes.data.find((c: any) => c.isDefault);
      if (def && !isView) setForm(f => ({ ...f, currencyCode: def.code }));
    } catch (err) { console.error('Currencies not available:', err); }
  };

  const loadEntry = async () => {
    try {
      const res = await accountingService.getJournalEntry(Number(id));
      setViewData(res.data);
      setForm({
        entryDate: res.data.entryDate.split('T')[0],
        reference: res.data.reference, description: res.data.description,
        currencyCode: res.data.currencyCode || 'USD'
      });
      if (res.data.lines) setLines(res.data.lines.map((l: any) => ({
        accountId: l.accountId, debitAmount: l.debitAmount,
        creditAmount: l.creditAmount, description: l.description
      })));
    } catch (err) { console.error(err); }
  };

  const updateLine = (index: number, field: keyof JournalLine, value: any) => {
    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'debitAmount' && Number(value) > 0) updated[index].creditAmount = 0;
    if (field === 'creditAmount' && Number(value) > 0) updated[index].debitAmount = 0;
    setLines(updated);
  };

  const addLine = () => setLines([...lines, { ...emptyLine }]);
  const removeLine = (i: number) => { if (lines.length > 2) setLines(lines.filter((_, idx) => idx !== i)); };

  const totalDebit = lines.reduce((s, l) => s + Number(l.debitAmount || 0), 0);
  const totalCredit = lines.reduce((s, l) => s + Number(l.creditAmount || 0), 0);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01;
  const sym = currencies.find(c => c.code === form.currencyCode)?.symbol || '$';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError('');
    if (!isBalanced) { setError(`Debits (${sym}${totalDebit.toFixed(2)}) must equal Credits (${sym}${totalCredit.toFixed(2)})`); return; }
    const validLines = lines.filter(l => l.accountId && (l.debitAmount > 0 || l.creditAmount > 0));
    if (validLines.length < 2) { setError('At least two lines are required'); return; }
    try {
      await accountingService.createJournalEntry({
        entryDate: form.entryDate, reference: form.reference,
        description: form.description, currencyCode: form.currencyCode, lines: validLines
      });
      navigate('/journal-entries');
    } catch (err: any) { setError(err.response?.data?.message || 'Error saving journal entry'); }
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4">{isView ? `Journal Entry: ${viewData?.entryNumber || ''}` : 'New Journal Entry'}</Typography>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/journal-entries')}>Back to List</Button>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {viewData && <Alert severity={viewData.status === 'POSTED' ? 'success' : viewData.status === 'VOID' ? 'error' : 'warning'} sx={{ mb: 2 }}>Status: {viewData.status}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField fullWidth required type="date" label={t('journal.entry_date')} value={form.entryDate}
          onChange={e => setForm({ ...form, entryDate: e.target.value })} InputLabelProps={{ shrink: true }} disabled={isView} />
        <TextField fullWidth label={t('journal.reference')} value={form.reference}
          onChange={e => setForm({ ...form, reference: e.target.value })} disabled={isView} />
        <TextField select label={t('common.currency')} value={form.currencyCode} sx={{ minWidth: 150 }}
          onChange={e => setForm({ ...form, currencyCode: e.target.value })} disabled={isView}>
          {currencies.map((c: any) => <MenuItem key={c.id} value={c.code}>{c.code} ({c.symbol})</MenuItem>)}
          {currencies.length === 0 && <MenuItem value="USD">USD ($)</MenuItem>}
        </TextField>
      </Box>
      <TextField fullWidth label={t('common.description')} value={form.description}
        onChange={e => setForm({ ...form, description: e.target.value })} sx={{ mb: 3 }} disabled={isView} />

      <Typography variant="h6" sx={{ mb: 1 }}>Entry Lines</Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>{t('journal.account')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>{t('common.description')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="right">{t('journal.debit')}</TableCell>
            <TableCell sx={{ fontWeight: 'bold', width: '15%' }} align="right">{t('journal.credit')}</TableCell>
            {!isView && <TableCell sx={{ width: 50 }} />}
          </TableRow></TableHead>
          <TableBody>
            {lines.map((line, idx) => (
              <TableRow key={idx}>
                <TableCell>{isView && viewData?.lines ? (
                  `${viewData.lines[idx]?.accountCode} - ${viewData.lines[idx]?.accountName}`
                ) : (
                  <TextField select fullWidth size="small" value={line.accountId} onChange={e => updateLine(idx, 'accountId', e.target.value)}>
                    <MenuItem value="">Select Account</MenuItem>
                    {accounts.map((a: any) => <MenuItem key={a.id} value={a.id}>{a.accountCode} - {a.accountName}</MenuItem>)}
                  </TextField>
                )}</TableCell>
                <TableCell><TextField fullWidth size="small" value={line.description}
                  onChange={e => updateLine(idx, 'description', e.target.value)} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.debitAmount || ''}
                  onChange={e => updateLine(idx, 'debitAmount', Number(e.target.value))}
                  inputProps={{ step: '0.01', min: '0' }} disabled={isView} /></TableCell>
                <TableCell><TextField fullWidth size="small" type="number" value={line.creditAmount || ''}
                  onChange={e => updateLine(idx, 'creditAmount', Number(e.target.value))}
                  inputProps={{ step: '0.01', min: '0' }} disabled={isView} /></TableCell>
                {!isView && <TableCell><IconButton size="small" color="error" onClick={() => removeLine(idx)}
                  disabled={lines.length <= 2}><Delete fontSize="small" /></IconButton></TableCell>}
              </TableRow>
            ))}
            <TableRow sx={{ bgcolor: isBalanced ? '#e8f5e9' : '#ffebee' }}>
              <TableCell colSpan={2} sx={{ fontWeight: 'bold' }}>Totals {!isBalanced && <span style={{ color: 'red' }}> — NOT BALANCED</span>}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{totalDebit.toFixed(2)}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>{sym}{totalCredit.toFixed(2)}</TableCell>
              {!isView && <TableCell />}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {!isView && (<>
        <Button startIcon={<Add />} onClick={addLine} sx={{ mt: 1 }}>{t('common.add_line')}</Button>
        <Box sx={{ mt: 3 }}><Button type="submit" variant="contained" startIcon={<Save />} size="large" disabled={!isBalanced}>{t('journal.create')}</Button></Box>
      </>)}
    </Box>
  );
};

export default JournalEntryForm;
